/**
 * Focus screens: timed, scored, on camera, or a full-screen editor. The
 * assistant stays out of them entirely. Not `isDoNotDisturbPath`: that list
 * also silences the store, where the assistant is wanted.
 */
const FOCUS_SCREENS = [
  /\/play(\/|$)/, // competition, skill, course, skill-test and multiplayer play
  /^\/skill-builder\/multiplayer\/[^/]+/, // a room or the arena
  /^\/interview\/[^/]+\/join/,
  /^\/course\/[^/]+\/test\//,
  /^\/course\/[^/]+\/read\//, // a lesson has its own AI Guide beside the cards
  /^\/course\/[^/]+\/intro\//, // the orientation deck: the orb sat over its Next button
  /^\/classes\/[^/]+\/quizzes\//, // a class quiz attempt: timed, graded by the teacher
  /^\/evaluation-assessment\//,
  /\/assessment(\/|$)/,
  /^\/onboarding/,
  /^\/candidate-post/,
  /^\/resume-builder\/(new|job-fit-preview|job-fit-app-preview|template-shot)/,
  /^\/cover-letter\/print/,
  /^\/coding-practice\/(?!category\/)[^/]+/, // the code editor, not the lists
];

export const isAssistantHiddenPath = (pathname: string) =>
  FOCUS_SCREENS.some((pattern) => pattern.test(pathname));

/** Pages with a bar pinned to the bottom on small screens: lift the orb over it. */
const BOTTOM_BAR_PAGES = [
  /^\/competition\/[^/]+\/?$/,
  /^\/job-search\/[^/]+\/?$/,
  /^\/applied-jobs\/[^/]+\/?$/,
  /^\/chat\/candidate/,
];

export const hasBottomBar = (pathname: string) =>
  BOTTOM_BAR_PAGES.some((pattern) => pattern.test(pathname));

interface PageHint {
  pattern: RegExp;
  label: string;
  suggestions: string[];
}

/** What the panel says it can see, and what it suggests asking there. */
const PAGE_HINTS: PageHint[] = [
  {
    pattern: /^\/competition\/[^/]+/,
    label: 'This competition',
    suggestions: [
      'When does this competition end?',
      'What can I win here?',
      'Register me for this',
    ],
  },
  {
    pattern: /^\/competition\/?$/,
    label: 'Competitions',
    suggestions: [
      "What's live right now?",
      'Which competitions am I registered for?',
      'How did I rank last time?',
    ],
  },
  {
    pattern: /^\/(job-search|applied-jobs)\/[^/]+/,
    label: 'This job',
    suggestions: [
      'Is this job a good fit for me?',
      'What does this role pay?',
      'Apply for me',
    ],
  },
  {
    pattern: /^\/job-search\/?$/,
    label: 'Find Jobs',
    suggestions: [
      'Find remote React jobs',
      'Show my saved jobs',
      'Turn on auto-apply',
    ],
  },
  {
    pattern: /^\/applied-jobs\/?$/,
    label: 'Applied Jobs',
    suggestions: [
      'Where do my applications stand?',
      'Any shortlists or interviews?',
      'Find more jobs like these',
    ],
  },
  {
    pattern: /^\/course\/[^/]+/,
    label: 'This course',
    suggestions: [
      'How far am I in this course?',
      'Do I get a certificate?',
      'What should I learn next?',
    ],
  },
  {
    pattern: /^\/(course|skill-builder)\/?$/,
    label: 'Learning',
    suggestions: [
      'Suggest a course for me',
      'Continue where I left off',
      "What are today's missions?",
    ],
  },
  {
    pattern: /^\/support/,
    label: 'Support',
    suggestions: [
      'I have a problem I need help with',
      'Check my support tickets',
      'Report a bug',
    ],
  },
  {
    pattern: /^\/store/,
    label: 'Store',
    suggestions: [
      'What do I get with Pro?',
      'How much does Pro cost?',
      'What is Spotlight+?',
    ],
  },
  {
    pattern: /^\/profile/,
    label: 'Your profile',
    suggestions: [
      "What's missing from my profile?",
      'How do I stand out to recruiters?',
      'Open the resume builder',
    ],
  },
  {
    pattern: /^\/resume-builder/,
    label: 'Resume Builder',
    suggestions: [
      'How do I make my resume ATS-friendly?',
      'Which templates are free?',
      'How do I tailor my resume to a job?',
    ],
  },
];

const GENERAL_SUGGESTIONS = [
  'Find jobs that match me',
  "What's live in competitions?",
  'How many credits do I have?',
  'I need help from support',
];

export const pageHint = (pathname: string) => {
  const hint = PAGE_HINTS.find((h) => h.pattern.test(pathname));
  return { label: hint?.label ?? null, suggestions: hint?.suggestions ?? [] };
};

/**
 * Cycled through the empty composer: things it does, not just answers. Kept
 * short: the input sizes itself to its placeholder, so a hint that wraps on a
 * phone would make it jump a line taller.
 */
export const TRY_SAYING = [
  'Find remote React jobs',
  "Claim today's missions",
  'Track my applications',
  'Suggest a SQL course',
  'Turn on auto-apply',
  'What can I win today?',
];

/** The chips under the latest reply: next steps on it, then other features. */
export interface FollowUpSet {
  options: string[];
  explore: string[];
}

const RELATED_FOLLOW_UPS = 3;
const EXPLORE_FOLLOW_UPS = 2;

/** Ways into a feature, each skipped on its own pages. */
const EXPLORE_SUGGESTIONS: { pattern: RegExp; text: string }[] = [
  { pattern: /^\/competition/, text: 'Any hiring challenge live now?' },
  { pattern: /^\/profile/, text: "What's missing from my profile?" },
  { pattern: /^\/rewards/, text: "Claim today's missions" },
  { pattern: /^\/job-search/, text: 'Find jobs that match me' },
  { pattern: /^\/course/, text: 'Suggest a course for my goal' },
  { pattern: /^\/applied-jobs/, text: 'Where do my applications stand?' },
  { pattern: /^\/skill-builder/, text: 'Quiz my friends live' },
  { pattern: /^\/resume-builder/, text: 'Make my resume ATS-friendly' },
  { pattern: /^\/competition/, text: 'What can I win this week?' },
];

const sameText = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Chips for a reply that came without its own (one from before follow-ups,
 * or the model left the line off): this page's questions, then other
 * features rotating each turn, never one already asked.
 */
export const fallbackFollowUps = (
  pathname: string,
  asked: string[],
  turn: number,
): FollowUpSet => {
  const fresh = (text: string) => !asked.some((a) => sameText(a, text));
  const hint = PAGE_HINTS.find((h) => h.pattern.test(pathname));
  const options = [...(hint?.suggestions ?? []), ...GENERAL_SUGGESTIONS]
    .filter(fresh)
    .slice(0, RELATED_FOLLOW_UPS);
  const pool = EXPLORE_SUGGESTIONS.filter(
    (s) => !s.pattern.test(pathname) && fresh(s.text),
  ).map((s) => s.text);
  const start = pool.length ? turn % pool.length : 0;
  const explore = [...pool.slice(start), ...pool.slice(0, start)]
    .filter((text) => !options.some((o) => sameText(o, text)))
    .slice(0, EXPLORE_FOLLOW_UPS);
  return { options, explore };
};

/**
 * A reply's own chips, filled out from `fallback` where it has fewer than
 * a full set. Without a fallback (a rescue chat) only its own are shown.
 */
export const resolveFollowUps = (
  own: FollowUpSet | null,
  fallback: FollowUpSet | null,
): FollowUpSet | null => {
  if (!fallback) return own;
  const options = own?.options.length ? own.options : fallback.options;
  const explore = [
    ...(own?.explore ?? []),
    ...fallback.explore.filter(
      (text) =>
        ![...options, ...(own?.explore ?? [])].some((o) => sameText(o, text)),
    ),
  ].slice(0, EXPLORE_FOLLOW_UPS);
  return options.length || explore.length ? { options, explore } : null;
};

/** While a reply is pending and silent this long, read it over HTTP instead. */
export const SOCKET_GRACE_MS = 6000;
export const POLL_INTERVAL_MS = 2500;
export const POLL_GIVE_UP_MS = 160000;

/** The reason the chat's own Pro checkout reports, so leaving it goes back to the chat. */
export const CHAT_CHECKOUT_REASON = 'assistant_rescue';

/** A chat the app opened after a Pro checkout was left: it offers only its own chips. */
export const RESCUE_CHAT_KIND = 'checkout_rescue';
